import React from 'react';

import {
  ErrorBoundary,
  SearchProvider,
  Results as AppSearchResults,
  WithSearch,
} from '@elastic/react-search-ui';
import { ConfigHelper } from '../../config/ConfigHelper';
import HeApiConnector from '../../connectors/HeApiConnector';
import { ConnectorProvider } from '../../connectors/ConnectorContext';
import { ParentProvider, useParent } from '../../contexts/ParentContext';
import DocumentMetaUpdater from '../../components/DocumentMetaUpdater';
import configJson from './archive-search-config.json';
import PagingRealistic from '../../components/PagingRealistic';
import ArchiveSearchResultView from './ArchiveSearchResultView';
import Layout from '../../components/Layout';
import ArchiveSearchFilters from './ArchiveSearchFilters';
import Results from '../../components/Results';
import ResultHeader from '../../components/ResultHeader';
import NoResults from '../../components/NoResults';
import Header from '../../components/Header';
import FiltersButton from '../../components/FiltersButton';
import ErrorBoundaryContent from '../../components/ErrorBoundaryContent';
import HelpFooter from '../../components/HelpFooter';

const configHelper = new ConfigHelper(configJson);

function ArchiveSearchContent() {
  const { setParent } = useParent();
  const connector = React.useMemo(
    () =>
      new HeApiConnector({
        ...configHelper.getEndpoints('ARCHIVE'),
        setParent,
      }),
    [setParent],
  );

  const config = React.useMemo(
    () => ({
      alwaysSearchOnInitialLoad: true,
      apiConnector: connector,
      initialState: {
        resultsPerPage: configHelper.getPageSizeOptions()[0],
      },
      searchQuery: {
        facets: configHelper.buildFacetConfigFromConfig(),
        ...configHelper.buildSearchOptionsFromConfig(),
        disjunctiveFacets: configHelper.buildDisjunctiveFacetsFromConfig(),
      },
    }),
    [connector],
  );

  return (
    <SearchProvider config={config}>
      <ConnectorProvider connector={connector}>
        <DocumentMetaUpdater searchName="Archive Collections" />
        <WithSearch
          mapContextToProps={({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
            resultSearchTerm,
          }) => ({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
            resultSearchTerm,
          })}
        >
          {({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
          }) => {
            return (
              <div className="search-ui" id="main-content">
                <ErrorBoundary view={ErrorBoundaryContent}>
                  <Layout
                    BodyContent={
                      <AppSearchResults
                        resultView={ArchiveSearchResultView}
                        shouldTrackClickThrough={true}
                        thumbnailField={configHelper.getThumbnailField()}
                        titleField={configHelper.getTitleField()}
                        urlField={configHelper.getUrlField()}
                        view={Results}
                      />
                    }
                    BodyFooter={
                      <>
                        <PagingRealistic />
                        <HelpFooter />
                      </>
                    }
                    BodyHeader={
                      <ResultHeader
                        configHelper={configHelper}
                        connector={connector}
                        filters={filters}
                        totalResults={totalResults}
                        wasSearched={wasSearched}
                      />
                    }
                    Filters={<ArchiveSearchFilters />}
                    Header={
                      <Header
                        FiltersButton={
                          <FiltersButton
                            SearchFilters={<ArchiveSearchFilters />}
                            clearFilters={clearFilters}
                            facets={facets}
                          />
                        }
                      />
                    }
                    NoResults={<NoResults />}
                    filters={filters}
                    isLoading={isLoading}
                    searchType="archive"
                    totalResults={totalResults}
                    wasSearched={wasSearched}
                  />
                </ErrorBoundary>
              </div>
            );
          }}
        </WithSearch>
      </ConnectorProvider>
    </SearchProvider>
  );
}

export default function ArchiveSearch() {
  return (
    <ParentProvider>
      <ArchiveSearchContent />
    </ParentProvider>
  );
}
